'use client'

import { type FC, useMemo, useState } from 'react'
import { KATANA_CHAIN_ID } from 'src/config'
import { useConcentratedPositionInfo } from 'src/lib/wagmi/hooks/positions/hooks/useConcentratedPositionInfo'
import {
  SUSHISWAP_V3_FACTORY_ADDRESS,
  type SushiSwapV3ChainId,
  computeSushiSwapV3PoolAddress,
  isEvmWNativeSupported,
} from 'sushi/evm'
import { useConnection } from 'wagmi'
import { ConcentratedLiquidityProvider } from '~evm/_ui/concentrated-liquidity-provider'
import { SelectPricesWidget } from '~evm/_ui/select-prices-widget'
import {
  ConcentratedLiquidityURLStateProvider,
  useConcentratedLiquidityURLState,
} from '../../_ui/concentrated-liquidity-url-state-provider'
import { SelectFeeConcentratedWidget } from '../../_ui/select-fee-concentrated-widget'
import { SelectTokensWidget } from '../../_ui/select-tokens-widget'
import { ConcentratedLiquidityWidget } from '../_ui/concentrated-liquidity-widget'

// TODO: Restore smart pool (Steer/Liquidity Vault) strategy selection when
// Katana smart pools API is available. Previously this page had a
// SelectV3PoolTypeWidget toggle between Manual and Smart modes, with
// SelectSmartPoolStrategyWidget and SmartPoolLiquidityWidget for the smart path.
// See docs/steer-api-response-shapes.md for the expected data shape.

export default function Page() {
  return (
    <ConcentratedLiquidityURLStateProvider
      chainId={KATANA_CHAIN_ID as SushiSwapV3ChainId}
    >
      <ConcentratedLiquidityProvider>
        <_Add />
      </ConcentratedLiquidityProvider>
    </ConcentratedLiquidityURLStateProvider>
  )
}

const _Add: FC = () => {
  const { address } = useConnection()
  const {
    chainId,
    token0,
    token1,
    setToken1,
    setToken0,
    feeAmount,
    setFeeAmount,
    tokensLoading,
    tokenId,
    switchTokens,
  } = useConcentratedLiquidityURLState()

  const [_invert, _setInvert] = useState(false)
  const { data: position } = useConcentratedPositionInfo({
    chainId,
    token0,
    tokenId,
    token1,
  })

  const poolAddress = useMemo(
    () =>
      token0 && token1 && feeAmount
        ? computeSushiSwapV3PoolAddress({
            factoryAddress: SUSHISWAP_V3_FACTORY_ADDRESS[chainId],
            tokenA: token0.wrap(),
            tokenB: token1.wrap(),
            fee: feeAmount,
          })
        : undefined,
    [chainId, feeAmount, token0, token1],
  )

  return (
    <>
      <SelectTokensWidget
        chainId={chainId}
        token0={token0}
        token1={token1}
        setToken0={setToken0}
        setToken1={setToken1}
        includeNative={isEvmWNativeSupported(chainId)}
      />
      <SelectFeeConcentratedWidget
        chainId={chainId}
        feeAmount={feeAmount}
        setFeeAmount={setFeeAmount}
        token1={token1}
        token0={token0}
      />
      <SelectPricesWidget
        chainId={chainId}
        token0={token0}
        token1={token1}
        poolAddress={poolAddress}
        tokenId={tokenId}
        feeAmount={feeAmount}
        switchTokens={switchTokens}
      />
      <ConcentratedLiquidityWidget
        chainId={chainId}
        account={address}
        token0={token0}
        token1={token1}
        setToken0={setToken0}
        setToken1={setToken1}
        feeAmount={feeAmount}
        tokensLoading={tokensLoading}
        existingPosition={position ?? undefined}
        tokenId={tokenId}
        successLink={`/pool/v3/${poolAddress}/${tokenId ?? 'positions'}`}
      />
    </>
  )
}
